import * as THREE from 'three';
import { GLTF_IMPORT_SCALE_FACTOR } from '../../../utils/constants';
import { calculateEndPoint } from '../calculateEndPoint';
import { scalePoint } from '../scaling/scalePoint';

export type MatrixData = {
  matrix: number[];
  extras: {
    length: number;
    depth: number;
  };
};

export const extractWallEndpoints = (data: MatrixData): { start: [number, number]; end: [number, number] } | false => {
  const { matrix, extras } = data;
  const length = extras.length / 2;
  const lengthOffset = extras.length / 4;
  const depth = extras.depth / 20;

  /* 
  just a reminder on how the transformation matrix looks like, for people who cant do math, like me, myself and i
    matrix[0]  = cosθ
    matrix[1]  = 0
    matrix[2]  = -sinθ
    matrix[3]  = 0

    matrix[4]  = 0
    matrix[5]  = 1
    matrix[6]  = 0
    matrix[7]  = 0

    matrix[8]  = sinθ
    matrix[9]  = 0
    matrix[10] = cosθ
    matrix[11] = 0

    matrix[12] = translation X 
    matrix[13] = translation Y 
    matrix[14] = translation Z 
    matrix[15] = 1
  */

  // using gltf convention, lets extract the position: x from matrix[12] and y from -matrix[14] (gltfs z becomes our 2D y)
  const startX = matrix[12];
  const startY = -matrix[14];

  // extract the rotation robustly (yaw) even when pitch/roll are present
  let rotation = Math.atan2(matrix[8], matrix[10]);

  // create the start point and scale it
  const startPoint = new THREE.Vector3(startX, startY, 0);
  const scaledStartPoint = scalePoint(startPoint, GLTF_IMPORT_SCALE_FACTOR);
  rotation = (rotation + 2 * Math.PI) % (2 * Math.PI);

  // calculate offsets along the wall’s length and perpendicular (depth) directions
  const offsetX = lengthOffset * Math.cos(rotation);
  const offsetY = lengthOffset * Math.sin(rotation);

  const depthOffsetX = depth * Math.cos(rotation + Math.PI / 2);
  const depthOffsetY = depth * Math.sin(rotation + Math.PI / 2);

  // adjust the start point by subtracting both offsets
  const scaledStartPointWithOffset = new THREE.Vector3(
    scaledStartPoint.x - offsetX - depthOffsetX,
    scaledStartPoint.y - offsetY - depthOffsetY,
    0,
  );

  // calculate the end point along the wall’s axis
  const endPoint = calculateEndPoint(scaledStartPointWithOffset, rotation, length);

  return {
    start: [scaledStartPointWithOffset.x, scaledStartPointWithOffset.y],
    end: [endPoint.x, endPoint.y],
  };
};
